import { SubType } from './../../../../../node_modules/typed-assert/src/index';
import {
  AccountDocument,
  AdminExtendedPaymentIntent,
  AdminService,
  PaymentMethod,
} from '@admin-api/index';
import { Component, Inject, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { MinorUnitsToCurrencyPipe } from 'app/core/pipes/minor-units-to-currency.pipe';
import { BehaviorSubject } from 'rxjs';

export interface IRequestInformationDialogReturnData {
  type: AccountDocument.TypeEnum;
  subtype: AccountDocument.SubtypeEnum;
  requestNotes?: string;
}

export interface IRequestInformationDialogData {
  accountId: string;
}

@Component({
  selector: 'app-request-information-dialog',
  templateUrl: './request-information-dialog.component.html',
  styleUrls: ['./request-information-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RequestInformationDialogComponent {
  @ViewChild('stepper') stepper: MatStepper;
  public writtenSubtypes = [
    AccountDocument.SubtypeEnum.BUSINESS_MODEL,
    AccountDocument.SubtypeEnum.PRICING_MODEL,
    AccountDocument.SubtypeEnum.WEBSITE,
    AccountDocument.SubtypeEnum.SHOPPING_CART,
    AccountDocument.SubtypeEnum.ADMIN_RETURN,
    AccountDocument.SubtypeEnum.INSUFFICIENT_FUNDS_RETURN,
    AccountDocument.SubtypeEnum.UNAUTHORIZED_RETURN,
    AccountDocument.SubtypeEnum.OTHER,
  ];
  public documentSubtypes = [
    AccountDocument.SubtypeEnum.BANK_LETTER_VOIDED_CHECK,
    AccountDocument.SubtypeEnum.EIN_LETTER,
    AccountDocument.SubtypeEnum.ARTICLES_OF_INCORP,
    AccountDocument.SubtypeEnum.BUSINESS_LICENSE,
    AccountDocument.SubtypeEnum.UTILITY_BILL,
    AccountDocument.SubtypeEnum.PROCESSING_STATEMENT,
    AccountDocument.SubtypeEnum.FINANCIAL_STATEMENT,
    AccountDocument.SubtypeEnum.BANK_STATEMENT,
    AccountDocument.SubtypeEnum._501C3,
    AccountDocument.SubtypeEnum.T3010,
    AccountDocument.SubtypeEnum.TAX_RETURN,
    AccountDocument.SubtypeEnum.INVOICE,
    AccountDocument.SubtypeEnum.DL_PASSPORT,
    AccountDocument.SubtypeEnum.DL_BACK,
    AccountDocument.SubtypeEnum.OTHER,
  ];

  public writtenForm: UntypedFormGroup;
  public documentForm: UntypedFormGroup;
  public writtenRequestForm: UntypedFormGroup;
  public documentRequestForm: UntypedFormGroup;
  public showInvoiceAmount: boolean = false;
  public showAdminReturnsAmount: boolean = false;
  public showInsufficientReturnsAmount: boolean = false;
  public showUnauthorizedReturnsAmount: boolean = false;
  public numberOfInvoices: number = 0;
  public numberOfAdminReturns: number = 0;
  public numberOfInsufficientFundsReturns: number = 0;
  public numberOfUnauthorizedReturns: number = 0;
  public invoiceControls: { control: string; label: string; index: number }[] =
    [];
  public adminReturnControl: {
    control: string;
    label: string;
    index: number;
  }[] = [];
  public insufficientFundsControl: {
    control: string;
    label: string;
    index: number;
  }[] = [];
  public unauthorizedReturnsControl: {
    control: string;
    label: string;
    index: number;
  }[] = [];
  public checkedWrittenSubtypes: AccountDocument.SubtypeEnum[] = [];
  public checkedDocumentSubtypes: AccountDocument.SubtypeEnum[] = [];

  public currentStep = 0;
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  public dialogTitle: string = 'Select additional information';
  public dialogSubtitle: string =
    'Request additional information from the merchant.';

  public accountId: string;
  private _paymentIntents$ = new BehaviorSubject<AdminExtendedPaymentIntent[]>(
    [],
  );
  constructor(
    public matDialogRef: MatDialogRef<RequestInformationDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public readonly dialogData: IRequestInformationDialogData,
    private fb: FormBuilder,
    private _adminService: AdminService,
    private _minorUnitsToCurrencyPipe: MinorUnitsToCurrencyPipe,
  ) {
    this.accountId = dialogData.accountId;
    this.writtenRequestForm = this.fb.group({});
    this.documentRequestForm = this.fb.group({});

    this.writtenForm = this.fb.group({
      [AccountDocument.SubtypeEnum.BUSINESS_MODEL.toString()]: [false],
      [AccountDocument.SubtypeEnum.PRICING_MODEL.toString()]: [false],
      [AccountDocument.SubtypeEnum.WEBSITE.toString()]: [false],
      [AccountDocument.SubtypeEnum.SHOPPING_CART.toString()]: [false],
      [AccountDocument.SubtypeEnum.ADMIN_RETURN.toString()]: [false],
      numberOfAdminReturns: [0, [Validators.min(0)]],
      [AccountDocument.SubtypeEnum.INSUFFICIENT_FUNDS_RETURN.toString()]: [
        false,
      ],
      numberOfInsufficientFundsReturns: [0, [Validators.min(0)]],
      [AccountDocument.SubtypeEnum.UNAUTHORIZED_RETURN.toString()]: [false],
      numberOfUnauthorizedReturns: [0, [Validators.min(0)]],
      [AccountDocument.SubtypeEnum.OTHER.toString()]: [false],
    });

    this.documentForm = this.fb.group({
      [AccountDocument.SubtypeEnum.BANK_LETTER_VOIDED_CHECK.toString()]: [
        false,
      ],
      [AccountDocument.SubtypeEnum.EIN_LETTER.toString()]: [false],
      [AccountDocument.SubtypeEnum.ARTICLES_OF_INCORP.toString()]: [false],
      [AccountDocument.SubtypeEnum.BUSINESS_LICENSE.toString()]: [false],
      [AccountDocument.SubtypeEnum.UTILITY_BILL.toString()]: [false],
      [AccountDocument.SubtypeEnum.PROCESSING_STATEMENT.toString()]: [false],
      [AccountDocument.SubtypeEnum.FINANCIAL_STATEMENT.toString()]: [false],
      [AccountDocument.SubtypeEnum.BANK_STATEMENT.toString()]: [false],
      [AccountDocument.SubtypeEnum._501C3.toString()]: [false],
      [AccountDocument.SubtypeEnum.T3010.toString()]: [false],
      [AccountDocument.SubtypeEnum.TAX_RETURN.toString()]: [false],
      [AccountDocument.SubtypeEnum.INVOICE.toString()]: [false],
      numberOfInvoices: [0, [Validators.min(0)]],
      [AccountDocument.SubtypeEnum.DL_PASSPORT.toString()]: [false],
      [AccountDocument.SubtypeEnum.DL_BACK.toString()]: [false],
      [AccountDocument.SubtypeEnum.OTHER.toString()]: [false],
    });
  }

  public cancelClicked() {
    this.matDialogRef.close(null);
  }

  public backPage() {
    this.checkedWrittenSubtypes = [];
    this.checkedDocumentSubtypes = [];
    this.writtenRequestForm = this.fb.group({});
    this.documentRequestForm = this.fb.group({});
    this.stepper.previous();
    if (this.currentStep > 0) {
      this.currentStep--;
    }
    this.getTitleAndSubtitle();
  }

  private getReturnTypeConfig(type: AccountDocument.SubtypeEnum): {
    controlArray: { control: string; label: string; index: number }[];
    contextLabel: string;
  } | null {
    switch (type) {
      case AccountDocument.SubtypeEnum.ADMIN_RETURN:
        return {
          controlArray: this.adminReturnControl,
          contextLabel: 'Admin ACH Return Context',
        };
      case AccountDocument.SubtypeEnum.INSUFFICIENT_FUNDS_RETURN:
        return {
          controlArray: this.insufficientFundsControl,
          contextLabel: 'Insufficient Funds ACH Return Context',
        };
      case AccountDocument.SubtypeEnum.UNAUTHORIZED_RETURN:
        return {
          controlArray: this.unauthorizedReturnsControl,
          contextLabel: 'Unauthorized ACH Return Context',
        };
      default:
        return null;
    }
  }

  private setupReturnTypeControls(
    type: AccountDocument.SubtypeEnum,
    count: number,
  ): void {

    const config = this.getReturnTypeConfig(type);
    if (!config) return;

    this.writtenRequestForm.addControl(
      `${type.toString()}_pi`,
      this.fb.control(''),
    );

    config.controlArray.push(
      ...[
        {
          control: `${type.toString()}_pi`,
          label: 'Payment Intent Id',
          index: 1,
        },
        {
          control: type.toString(),
          label: config.contextLabel,
          index: 1,
        },
      ],
    );

    if (count > 1) {
      for (let i = 1; i < count; i++) {
        const controlNumber = i + 1;
        const addedControls: {
          control: string;
          label: string;
          index: number;
        }[] = [];

        this.writtenRequestForm.addControl(
          `${type.toString()}_pi${controlNumber}`,
          this.fb.control(''),
        );

        this.writtenRequestForm.addControl(
          `${type.toString()}${controlNumber}`,
          this.fb.control(''),
        );

        addedControls.push({
          control: `${type.toString()}_pi${controlNumber}`,
          label: 'Payment Intent Id',
          index: controlNumber,
        });

        addedControls.push({
          control: `${type.toString()}${controlNumber}`,
          label: config.contextLabel,
          index: controlNumber,
        });

        config.controlArray.push(...addedControls);
      }
    }
  }
  
  public nextPage() {
    this.writtenRequestForm.reset();
    this.documentRequestForm.reset();
    this.invoiceControls = [];
    if (
      this.documentForm.get('invoice').value &&
      this.documentForm.get('numberOfInvoices').value > 1
    ) {
      this.numberOfInvoices = this.documentForm.get('numberOfInvoices').value;
    } else {
      this.numberOfInvoices = 1;
    }

    if (
      this.writtenForm.get('admin_return').value &&
      this.writtenForm.get('numberOfAdminReturns').value > 1
    ) {
      this.numberOfAdminReturns = this.writtenForm.get(
        'numberOfAdminReturns',
      ).value;
    } else {
      this.numberOfAdminReturns = 1;
    }

    if (
      this.writtenForm.get('insufficient_funds_return').value &&
      this.writtenForm.get('numberOfInsufficientFundsReturns').value > 1
    ) {
      this.numberOfInsufficientFundsReturns = this.writtenForm.get(
        'numberOfInsufficientFundsReturns',
      ).value;
    } else {
      this.numberOfInsufficientFundsReturns = 1;
    }

    if (
      this.writtenForm.get('unauthorized_return').value &&
      this.writtenForm.get('numberOfUnauthorizedReturns').value > 1
    ) {
      this.numberOfUnauthorizedReturns = this.writtenForm.get(
        'numberOfUnauthorizedReturns',
      ).value;
    } else {
      this.numberOfUnauthorizedReturns = 1;
    }

    this.writtenForm.markAllAsTouched();
    this.documentForm.markAllAsTouched();
    if (this.writtenForm.invalid || this.documentForm.invalid) {
      return;
    }

    for (const subtype of this.writtenSubtypes) {
      const control = this.writtenForm.get(subtype.toString());
      if (control && control.value) {
        this.checkedWrittenSubtypes.push(subtype);
      }
    }
    for (const subtype of this.documentSubtypes) {
      const control = this.documentForm.get(subtype.toString());
      if (control && control.value) {
        this.checkedDocumentSubtypes.push(subtype);
      }
    }

    for (const type of this.checkedWrittenSubtypes) {
      if (type === AccountDocument.SubtypeEnum.OTHER) {
        this.writtenRequestForm.addControl(
          type.toString(),
          this.fb.control('', [Validators.required]),
        );
      } else {
        this.writtenRequestForm.addControl(
          type.toString(),
          this.fb.control(''),
        );
        if (type === AccountDocument.SubtypeEnum.ADMIN_RETURN) {
          this.setupReturnTypeControls(type, this.numberOfAdminReturns);
        } else if (type === AccountDocument.SubtypeEnum.INSUFFICIENT_FUNDS_RETURN) {
          this.setupReturnTypeControls(type, this.numberOfInsufficientFundsReturns);
        } else if (type === AccountDocument.SubtypeEnum.UNAUTHORIZED_RETURN) {
          this.setupReturnTypeControls(type, this.numberOfUnauthorizedReturns);
        }
      }
    }
    for (const type of this.checkedDocumentSubtypes) {
      if (type === AccountDocument.SubtypeEnum.OTHER) {
        this.documentRequestForm.addControl(
          type.toString(),
          this.fb.control('', [Validators.required]),
        );
      } else {
        this.documentRequestForm.addControl(
          type.toString(),
          this.fb.control(''),
        );
        if (type === AccountDocument.SubtypeEnum.INVOICE) {
          this.documentRequestForm.addControl(
            `${type.toString()}_pi`,
            this.fb.control(''),
          );
          this.invoiceControls.push(
            ...[
              {
                control: `${type.toString()}_pi`,
                label: 'Payment Intent Id',
                index: 1,
              },
              {
                control: type.toString(),
                label: 'Invoice context',
                index: 1,
              },
            ],
          );
        }
      }
      if (
        type === AccountDocument.SubtypeEnum.INVOICE &&
        this.numberOfInvoices > 1
      ) {
        for (let i = 1; i < this.numberOfInvoices; i++) {
          const addedControls: {
            control: string;
            label: string;
            index: number;
          }[] = [];
          const controlNumber = i + 1;
          this.documentRequestForm.addControl(
            `invoice_pi${controlNumber}`,
            this.fb.control(''),
          );
          this.documentRequestForm.addControl(
            `invoice${controlNumber}`,
            this.fb.control(''),
          );

          addedControls.push({
            control: `invoice_pi${controlNumber}`,
            label: 'Payment Intent Id',
            index: controlNumber,
          });
          addedControls.push({
            control: `invoice${controlNumber}`,
            label: 'Invoice context',
            index: controlNumber,
          });

          this.invoiceControls.push(...addedControls);
        }
      }
    }

    this.invoiceControls = this.invoiceControls.filter(
      (control, index, self) =>
        index ===
        self.findIndex(
          (t) => t.control === control.control && t.index === control.index,
        ),
    );

    this.adminReturnControl = this.adminReturnControl.filter(
      (control, index, self) =>
        index ===
        self.findIndex(
          (t) => t.control === control.control && t.index === control.index,
        ),
    );

    this.insufficientFundsControl = this.insufficientFundsControl.filter(
      (control, index, self) =>
        index ===
        self.findIndex(
          (t) => t.control === control.control && t.index === control.index,
        ),
    );

    this.unauthorizedReturnsControl = this.unauthorizedReturnsControl.filter(
      (control, index, self) =>
        index ===
        self.findIndex(
          (t) => t.control === control.control && t.index === control.index,
        ),
    );
    this.stepper.next();
    this.currentStep++;
    this.getTitleAndSubtitle();
  }

  private collectFormData(
    controls: { control: string; label: string; index: number }[],
    formGroup: UntypedFormGroup,
  ): Record<string, Record<string, string>> {
    const data = {};
    for (const control of controls) {
      if (!data[control.index]) {
        data[control.index] = {};
      }
      data[control.index][control.control] = formGroup.get(
        control.control,
      ).value;
    }
    return data;
  }

  private processControlGroup(
    controlData: Record<string, Record<string, string>>,
    documentType: AccountDocument.TypeEnum,
    subtype: AccountDocument.SubtypeEnum,
  ): IRequestInformationDialogReturnData[] {
    const returnData: IRequestInformationDialogReturnData[] = [];

    for (const [index, control] of Object.entries(controlData)) {
      const piControl = Object.keys(control).find((key) => key.includes('_pi'));
      const contextControl = Object.keys(control).find(
        (key) => !key.includes('_pi'),
      );
      returnData.push({
        type: documentType,
        subtype: subtype,
        requestNotes: this.constructRequestNotes(
          this._paymentIntents$.value.find(
            (pi) => pi.id === control[piControl],
          ),
          control[contextControl],
          documentType === AccountDocument.TypeEnum.WRITTEN
            ? subtype
            : undefined,
        ),
      });
    }
    return returnData;
  }

  public async submitClicked() {
    if (this.writtenRequestForm.invalid || this.documentRequestForm.invalid) {
      return;
    }

    const returnData: IRequestInformationDialogReturnData[] = [];

    const invoiceControlData = this.collectFormData(
      this.invoiceControls,
      this.documentRequestForm,
    );
    const adminReturnControlData = this.collectFormData(
      this.adminReturnControl,
      this.writtenRequestForm,
    );
    const insufficientFundsControlData = this.collectFormData(
      this.insufficientFundsControl,
      this.writtenRequestForm,
    );
    const unauthorizedReturnControlData = this.collectFormData(
      this.unauthorizedReturnsControl,
      this.writtenRequestForm,
    );

    const paymentIntentIds = [
      ...Object.values(invoiceControlData),
      ...Object.values(adminReturnControlData),
      ...Object.values(insufficientFundsControlData),
      ...Object.values(unauthorizedReturnControlData),
    ]
      .flatMap((control) =>
        Object.keys(control)
          .filter((key) => key.includes('_pi'))
          .map((key) => control[key]),
      )
      .filter((id) => id && id.trim() !== '');

    if (paymentIntentIds.length > 0) {
      await this.getPaymentIntentData(paymentIntentIds);
    }

    returnData.push(
      ...this.processControlGroup(
        invoiceControlData,
        AccountDocument.TypeEnum.FILE,
        AccountDocument.SubtypeEnum.INVOICE,
      ),
      ...this.processControlGroup(
        adminReturnControlData,
        AccountDocument.TypeEnum.WRITTEN,
        AccountDocument.SubtypeEnum.ADMIN_RETURN,
      ),
      ...this.processControlGroup(
        insufficientFundsControlData,
        AccountDocument.TypeEnum.WRITTEN,
        AccountDocument.SubtypeEnum.INSUFFICIENT_FUNDS_RETURN,
      ),
      ...this.processControlGroup(
        unauthorizedReturnControlData,
        AccountDocument.TypeEnum.WRITTEN,
        AccountDocument.SubtypeEnum.UNAUTHORIZED_RETURN,
      ),
    );

    for (const [subtype, value] of Object.entries(
      this.writtenRequestForm.value,
    )) {
      if (
        !subtype.includes('admin_return') &&
        !subtype.includes('insufficient_funds_return') &&
        !subtype.includes('unauthorized_return') &&
        !subtype.startsWith('numberOf')
      ) {
        returnData.push({
          type: AccountDocument.TypeEnum.WRITTEN,
          subtype: subtype as AccountDocument.SubtypeEnum,
          requestNotes: (value as string)?.trim(),
        });
      }
    }

    for (const [subtype, value] of Object.entries(
      this.documentRequestForm.value,
    )) {
      if (!subtype.includes('invoice')) {
        returnData.push({
          type: AccountDocument.TypeEnum.FILE,
          subtype: subtype as AccountDocument.SubtypeEnum,
          requestNotes: (value as string)?.trim(),
        });
      }
    }

    this.matDialogRef.close(returnData.length > 0 ? returnData : null);
  }

  public mapSubtypeToTitle(subtype: AccountDocument.SubtypeEnum): string {
    switch (subtype) {
      //file
      case AccountDocument.SubtypeEnum.BANK_LETTER_VOIDED_CHECK:
        return 'Bank Letter or Voided Check';
      case AccountDocument.SubtypeEnum.EIN_LETTER:
        return 'EIN Letter';
      case AccountDocument.SubtypeEnum.ARTICLES_OF_INCORP:
        return 'Articles of Incorporation';
      case AccountDocument.SubtypeEnum.BUSINESS_LICENSE:
        return 'Business License';
      case AccountDocument.SubtypeEnum.UTILITY_BILL:
        return 'Utility Bill';
      case AccountDocument.SubtypeEnum.PROCESSING_STATEMENT:
        return 'Processing Statement (past 3 - 6 months)';
      case AccountDocument.SubtypeEnum.FINANCIAL_STATEMENT:
        return 'Financial Statement (past 1 - 2 years, include P&L Statement and Balance Sheet)';
      case AccountDocument.SubtypeEnum.BANK_STATEMENT:
        return 'Bank Statements (past 3 - 6 months)';
      case AccountDocument.SubtypeEnum._501C3:
        return '501c3';
      case AccountDocument.SubtypeEnum.T3010:
        return 'T3010';
      case AccountDocument.SubtypeEnum.TAX_RETURN:
        return 'Tax Return';
      case AccountDocument.SubtypeEnum.INVOICE:
        return 'Invoice';
      case AccountDocument.SubtypeEnum.DL_PASSPORT:
        return 'Driver’s License/Passport';
      case AccountDocument.SubtypeEnum.DL_BACK:
        return 'Driver’s License - Back side';
      //written
      case AccountDocument.SubtypeEnum.BUSINESS_MODEL:
        return 'Business Model Details';
      case AccountDocument.SubtypeEnum.PRICING_MODEL:
        return 'Pricing Model Details';
      case AccountDocument.SubtypeEnum.WEBSITE:
        return 'Website';
      case AccountDocument.SubtypeEnum.SHOPPING_CART:
        return 'No Shopping Cart';
      case AccountDocument.SubtypeEnum.ADMIN_RETURN:
        return 'Admin ACH/EFT Return';
      case AccountDocument.SubtypeEnum.INSUFFICIENT_FUNDS_RETURN:
        return 'Insufficient Funds ACH/EFT Return';
      case AccountDocument.SubtypeEnum.UNAUTHORIZED_RETURN:
        return 'Unauthorized ACH/EFT Return';
      case AccountDocument.SubtypeEnum.OTHER:
        return 'Other (requires additional notes)';
      default:
        return '';
    }
  }

  public defaultRequestNoteValue(subtype: AccountDocument.SubtypeEnum): void {
    switch (subtype) {
      case AccountDocument.SubtypeEnum.TAX_RETURN:
        const priorYear = new Date().getFullYear() - 1;
        this.documentRequestForm.get(subtype).setValue(priorYear.toString());
        break;
      default:
        return;
    }
  }

  public getTitleAndSubtitle(): void {
    switch (this.currentStep) {
      case 0:
        this.dialogTitle = 'Select additional information';
        this.dialogSubtitle =
          'Request additional information from the merchant.';
        break;
      case 1:
        this.dialogTitle = 'Add a note (Optional)';
        this.dialogSubtitle =
          'Add a note to each underwriting requirement to provide additional context to the merchant about the underwriting requirements. The note will be displayed in the email notification to the merchant and in the merchant app. Leave blank if no additional context is needed.';
        break;
      case 2:
        this.dialogTitle = 'Add a note (Optional)';
        this.dialogSubtitle =
          'Add a note to each underwriting requirement to provide additional context to the merchant about the underwriting requirements. The note will be displayed in the email notification to the merchant and in the merchant app. Leave blank if no additional context is needed.';
        break;
      default:
        return;
    }
  }
  public onCheckboxChange(
    checkbox: MatCheckboxChange,
    controlName: string,
  ): void {
    if (controlName === 'invoice') {
      const numberOfInvoices = this.documentForm.get('numberOfInvoices');
      if (checkbox.checked) {
        this.showInvoiceAmount = true;
        this.documentForm.get('numberOfInvoices').setValue(1);
        numberOfInvoices.setValidators([
          Validators.required,
          Validators.min(0),
        ]);
        numberOfInvoices.setValue(null);
        numberOfInvoices.updateValueAndValidity();
      } else {
        numberOfInvoices.clearValidators();
        numberOfInvoices.setValue(null);
        numberOfInvoices.updateValueAndValidity();
        this.showInvoiceAmount = false;
        this.invoiceControls = [];
      }
    }
    if (controlName === 'admin_return') {
      const numberOfAdminReturns = this.writtenForm.get('numberOfAdminReturns');
      if (checkbox.checked) {
        this.writtenForm.get('numberOfAdminReturns').setValue(1);
        this.showAdminReturnsAmount = true;
        numberOfAdminReturns.setValidators([
          Validators.required,
          Validators.min(0),
        ]);
        numberOfAdminReturns.setValue(null);
        numberOfAdminReturns.updateValueAndValidity();
      } else {
        numberOfAdminReturns.clearValidators();
        numberOfAdminReturns.setValue(null);
        numberOfAdminReturns.updateValueAndValidity();
        this.showAdminReturnsAmount = false;
        this.adminReturnControl = [];
      }
    }
    if (controlName === 'insufficient_funds_return') {
      const numberOfInsufficientFundsReturns = this.writtenForm.get(
        'numberOfInsufficientFundsReturns',
      );
      if (checkbox.checked) {
        this.writtenForm.get('numberOfInsufficientFundsReturns').setValue(1);
        this.showInsufficientReturnsAmount = true;
        numberOfInsufficientFundsReturns.setValidators([
          Validators.required,
          Validators.min(0),
        ]);
        numberOfInsufficientFundsReturns.setValue(null);
        numberOfInsufficientFundsReturns.updateValueAndValidity();
      } else {
        numberOfInsufficientFundsReturns.clearValidators();
        numberOfInsufficientFundsReturns.setValue(null);
        numberOfInsufficientFundsReturns.updateValueAndValidity();
        this.showInsufficientReturnsAmount = false;
        this.insufficientFundsControl = [];
      }
    }

    if (controlName === 'unauthorized_return') {
      const numberOfUnauthorizedReturns = this.writtenForm.get(
        'numberOfUnauthorizedReturns',
      );
      if (checkbox.checked) {
        this.writtenForm.get('numberOfUnauthorizedReturns').setValue(1);
        this.showUnauthorizedReturnsAmount = true;
        numberOfUnauthorizedReturns.setValidators([
          Validators.required,
          Validators.min(0),
        ]);
        numberOfUnauthorizedReturns.setValue(null);
        numberOfUnauthorizedReturns.updateValueAndValidity();
      } else {
        numberOfUnauthorizedReturns.clearValidators();
        numberOfUnauthorizedReturns.setValue(null);
        numberOfUnauthorizedReturns.updateValueAndValidity();
        this.showUnauthorizedReturnsAmount = false;
        this.unauthorizedReturnsControl = [];
      }
    }
  }

  public constructRequestNotes(
    paymentIntent: AdminExtendedPaymentIntent,
    context: string,
    subtype?: AccountDocument.SubtypeEnum,
  ): string {
    let notes = '';

    const dateFormat: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour12: true,
      minute: '2-digit',
      hour: 'numeric',
      second: '2-digit',
    };

    const isACHReturnType =
      subtype === AccountDocument.SubtypeEnum.ADMIN_RETURN ||
      subtype === AccountDocument.SubtypeEnum.INSUFFICIENT_FUNDS_RETURN ||
      subtype === AccountDocument.SubtypeEnum.UNAUTHORIZED_RETURN;

    if (paymentIntent) {
      notes += `${
        isACHReturnType ? 'ACH Amount:' : 'Invoice Amount:'
      } ${this._minorUnitsToCurrencyPipe.transform(
        paymentIntent.amount,
        paymentIntent.currency,
      )} | Date: ${new Date(paymentIntent.created_at).toLocaleString(
        'en-US',
        dateFormat,
      )} | Cardholder Name: ${this.getCardHolderName(
        paymentIntent,
      )} | Payment ID: ${paymentIntent.id}`;
    }
    if (context) {
      notes += notes
        ? ` | Additional notes: ${context}`
        : `Additional notes: ${context}`;
    }
    return notes;
  }

  private async getPaymentIntentData(
    paymentIntentIds: string[],
  ): Promise<void> {
    if (paymentIntentIds?.length === 0) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      this.isLoading$.next(true);
      this._adminService
        .adminListPaymentIntents({
          accountId: this.accountId,
          ids: paymentIntentIds,
        })
        .subscribe({
          next: (res) => {
            this._paymentIntents$.next(res.items || []);
          },
          error: (err) => {
            this._paymentIntents$.next([]);
            resolve();
          },
          complete: () => {
            this.isLoading$.next(false);
            resolve();
          },
        });
    });
  }
  private getCardHolderName(payment: AdminExtendedPaymentIntent): string {
    if (payment.payment_method?.type === PaymentMethod.TypeEnum.CARD) {
      return payment.payment_method?.card
        ? payment.payment_method?.card?.holder_name
          ? payment.payment_method?.card?.holder_name
          : '-'
        : payment.payment_method?.billing_details?.name
        ? payment.payment_method?.billing_details?.name
        : '-';
    } else if (
      payment.payment_method?.type === PaymentMethod.TypeEnum.CARD_PRESENT
    ) {
      return payment.payment_method?.card_present
        ? payment.payment_method?.card_present?.holder_name
          ? payment.payment_method?.card_present?.holder_name
          : '-'
        : payment.payment_method?.billing_details?.name
        ? payment.payment_method?.billing_details?.name
        : '-';
    }
    return payment.payment_method?.billing_details?.name
      ? payment.payment_method?.billing_details?.name
      : '-';
  }
}
